import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ROUTES, WLIDS } from 'Constants';
import { CustomerSelectionLocationState } from 'Models/_HumanWritten/LocationState';
import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { upperCaseFirst } from 'Util/StringUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import {
	formatCustomerName,
	formatCustomerOption,
	sortCustomersByName,
} from 'Views/Components/_HumanWritten/CustomerSelection/CustomerOptionFormatter';
import { searchCustomersByNameEmailPhone } from 'Views/Components/_HumanWritten/CustomerSelection/CustomerSelectionUtils';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { UserDisabledTag, UserDisplayNameTag } from 'Views/Components/_HumanWritten/UserDisplayNameTag';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import If from 'Views/Components/If/If';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import Skeleton from 'Views/Components/_HumanWritten/Skeleton/Skeleton';
import GuestSetup from './GuestSetup';

export interface CustomerSelectionProps {
	model: any;
	modelProperty: string;
	/**
	 * The url to return to when completing the register new customer flow.
	 */
	backUrl: string;
	/**
	 * Show the mobile/phone row in the customer details card. Defaults to false.
	 * Pass `appConfigStore.userPhoneEnabled` (ferry) or `appConfigStore.eventsPhoneEnabled` (events).
	 */
	phoneEnabled?: boolean;
	/**
	 * Optional hooks for callers that need to react to selection changes or surface
	 * field-level errors. The shape predates the AsyncSelectInput migration; only
	 * `errors` and `onAfterChange` are honoured here.
	 */
	combobox?: {
		errors?: string | string[];
		onAfterChange?: (event: unknown, data: { value: string }) => void;
	};
	onSelect?: (customer?: UserEntity) => void;
	disabled: boolean;
}

function CustomerSelection({
	model,
	modelProperty,
	backUrl,
	phoneEnabled = false,
	combobox = {},
	onSelect,
	disabled,
}: CustomerSelectionProps) {
	const navigate = useNavigate();
	const intl = useIntl();

	const [state, setState] = useState<'normal' | 'guest'>('normal');
	const [isLoading, setIsLoading] = useState(!!model[modelProperty]);
	const [customer, setCustomer] = useState<UserEntity | null>(null);
	const preloadedUser = useRef<UserEntity | undefined>();

	const additionalInfoLabel = intl.formatMessage({
		id: customer?.userDisplayName?.additionalInformationLabelKey || WLIDS.customerTab.userAdditionalInformation,
		defaultMessage: 'Additional information',
	});

	// Customer identity display. A company (when present) is always surfaced: as the primary
	// identity with the person shown as the contact name when the display config is on, otherwise
	// the person's name stays primary and the company shows as its own field below.
	const customerFullName =
		`${upperCaseFirst((customer?.firstName ?? '').toLowerCase())} ` +
		`${upperCaseFirst((customer?.lastName ?? '').toLowerCase())}`;
	const customerCompany = stringNotEmpty(customer?.companyName) ? customer!.companyName!.trim() : undefined;
	const showCompanyAsPrimary = !!customerCompany && appConfigStore.displayCompanyNameInsteadOfName;

	const { loadOptions: baseLoadOptions } = useCustomerSearch();

	const loadOptions = useCallback(
		async (query: string): Promise<UserEntity[]> => {
			const results = await baseLoadOptions(query);
			const sorted = [...results].sort(sortCustomersByName);
			if (preloadedUser.current && !sorted.some(x => x.id === preloadedUser.current?.id) && !query) {
				sorted.push(preloadedUser.current);
			}
			return sorted;
		},
		[baseLoadOptions],
	);

	const handleCustomerSelection = (selectedCustomer: UserEntity | null) => {
		setCustomer(selectedCustomer);
		if (onSelect) {
			onSelect(selectedCustomer ?? undefined);
		}
	};

	// On mount, hydrate the selected customer when the model already holds an id.
	useEffect(() => {
		let cancelled = false;
		const id = model[modelProperty];
		if (!id) {
			setIsLoading(false);
			return () => undefined;
		}
		(async () => {
			const results = await searchCustomersByNameEmailPhone('', id);
			if (cancelled) {
				return;
			}
			const match = results.find(x => x.id === id);
			if (match) {
				handleCustomerSelection(match);
			}
			setIsLoading(false);
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onChange = (option: unknown) => {
		const selected = (option as UserEntity | null) ?? null;
		runInAction(() => {
			model[modelProperty] = selected?.id ?? '';
		});
		handleCustomerSelection(selected);
		combobox.onAfterChange?.({}, { value: selected?.id ?? '' });
	};

	const formatOptionLabel = formatCustomerOption;

	const error = Array.isArray(combobox.errors) ? combobox.errors.find(Boolean) : combobox.errors;

	if (state === 'guest') {
		return (
			<div className="login-flow">
				<h4>Guest details</h4>
				{combobox.errors && <p>Please complete form.</p>}
				<GuestSetup
					onBack={() => setState('normal')}
					onSuccess={(user: UserEntity) => {
						runInAction(() => {
							preloadedUser.current = user;
							model[modelProperty] = user.id;
							handleCustomerSelection(user);
							setState('normal');
							combobox.onAfterChange?.({}, { value: user.id });
						});
					}}
				/>
			</div>
		);
	}

	return (
		<>
			{!disabled && (
				<div data-test-id="customer-selection-search">
					<AsyncSelectInput
						className="customer-selection__customer-select"
						label="Select an existing customer"
						placeholder="Search by name, email or phone"
						loadOptions={loadOptions}
						defaultOptions
						cacheOptions
						isClearable
						value={customer}
						onChange={onChange}
						getOptionLabel={(option: any) => formatCustomerName(option as UserEntity)}
						getOptionValue={(option: any) => (option as UserEntity).id}
						isOptionDisabled={(option: any) => (option as UserEntity).disabled}
						formatOptionLabel={formatOptionLabel as any}
						error={error}
					/>
				</div>
			)}
			{isLoading && (
				<div className="customer-selection__details" aria-busy="true" data-test-id="customer-selection-loading">
					<p className="customer-selection__details-title">Customer details</p>
					<div className="customer-selection__details-identity">
						<Skeleton width="12rem" height="1.5rem" />
					</div>
					<div className="customer-selection__details-row">
						<p className="customer-selection__details-row-title">Email</p>
						<div>
							<Skeleton width="14rem" height="1rem" />
						</div>
					</div>
					{phoneEnabled && (
						<div className="customer-selection__details-row">
							<p className="customer-selection__details-row-title">Mobile</p>
							<div>
								<Skeleton width="8rem" height="1rem" />
							</div>
						</div>
					)}
				</div>
			)}
			{!isLoading && customer && (
				<div className="customer-selection__details">
					<p className="customer-selection__details-title">Customer details</p>
					<div className="customer-selection__details-identity">
						<p className="customer-selection__details-name">
							{showCompanyAsPrimary ? customerCompany : customerFullName}
						</p>
						{(customer.isGuest || customer.userDisplayName?.displayName || customer.disabled) && (
							<div className="customer-selection__details-tags">
								{customer.isGuest && <GuestTag />}
								{UserDisplayNameTag(customer?.userDisplayName?.displayName)}
								{customer.disabled && UserDisabledTag()}
							</div>
						)}
					</div>
					{customerCompany &&
						(showCompanyAsPrimary ? (
							<div className="customer-selection__details-row">
								<p className="customer-selection__details-row-title">Contact name</p>
								<div>
									<p className="customer-selection__details-row-value">{customerFullName}</p>
								</div>
							</div>
						) : (
							<div className="customer-selection__details-row">
								<p className="customer-selection__details-row-title">Company</p>
								<div>
									<p className="customer-selection__details-row-value">{customerCompany}</p>
								</div>
							</div>
						))}
					<div className="customer-selection__details-row">
						<p className="customer-selection__details-row-title">Email</p>
						<div>
							<p className="customer-selection__details-row-value">{customer.email}</p>
						</div>
					</div>
					{phoneEnabled && (
						<div className="customer-selection__details-row">
							<p className="customer-selection__details-row-title">Mobile</p>
							<div>
								<p className="customer-selection__details-row-value">
									{customer.phone ? (
										customer.phone
									) : (
										<span className="customer-selection__details-row-empty">Not provided</span>
									)}
								</p>
							</div>
						</div>
					)}
					{customer.additionalInformation && (
						<div className="customer-selection__details-row">
							<p className="customer-selection__details-row-title">{additionalInfoLabel}</p>
							<div>
								<p className="customer-selection__details-row-value">
									{customer.additionalInformation ?? ''}
								</p>
							</div>
						</div>
					)}
				</div>
			)}

			<If condition={!disabled && !isLoading}>
				<div className="booking-section-or-divider" role="separator" aria-label="or">
					<span className="booking-section-or-divider__line" />
					<span className="booking-section-or-divider__label">OR</span>
					<span className="booking-section-or-divider__line" />
				</div>
				<Flex
					className={`customer-selection__action-btns${
						appConfigStore.guestBookingsEnabled ? '' : ' customer-selection__action-btns--single'
					}`}
					direction="row"
					gap="xs"
					flexWrap="wrap"
					flex={1}
				>
					<Button
						className="customer-selection__new-customer-btn"
						display={Display.Solid}
						sizes={Sizes.Medium}
						colors={Colors.Secondary}
						onClick={() => {
							const locationState: CustomerSelectionLocationState = { backUrl };
							navigate(ROUTES.REGISTER_CUSTOMER, { state: locationState });
						}}
						newStyles
						buttonProps={{ 'data-test-id': 'customer-selection-create-account-button' } as any}
					>
						Add new customer
					</Button>
					{appConfigStore.guestBookingsEnabled && (
						<Button
							display={Display.Solid}
							sizes={Sizes.Medium}
							colors={Colors.Primary}
							onClick={() => setState('guest')}
							buttonProps={{ 'data-test-id': 'staff-guest-booking-button' } as any}
							newStyles
						>
							Guest booking
						</Button>
					)}
				</Flex>
			</If>
		</>
	);
}

export default observer(CustomerSelection);
